import {
  CognitoUserPool, CognitoUser, AuthenticationDetails, type CognitoUserSession,
} from "amazon-cognito-identity-js";
import { config } from "./config";

const pool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

export interface AuthUser { email: string; role: "OWNER" | "STAFF" }

function userFromSession(session: CognitoUserSession): AuthUser {
  const claims = session.getIdToken().decodePayload() as Record<string, unknown>;
  const groups = (claims["cognito:groups"] as string[] | undefined) ?? [];
  return {
    email: String(claims.email ?? ""),
    role: groups.includes("OWNER") ? "OWNER" : "STAFF",
  };
}

/** SRP sign-in. Resolves with the user + id token (sent as the API bearer token). */
export function signIn(email: string, password: string): Promise<{ user: AuthUser; token: string }> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(details, {
      onSuccess: (session) => resolve({ user: userFromSession(session), token: session.getIdToken().getJwtToken() }),
      onFailure: (err) => reject(err),
      // permanent password is set server-side, so this path shouldn't trigger; handle defensively.
      newPasswordRequired: () => reject(new Error("A new password is required for this account.")),
    });
  });
}

/** Restore a session on page load (tokens are persisted by amazon-cognito-identity-js). */
export function restoreSession(): Promise<{ user: AuthUser; token: string } | null> {
  return new Promise((resolve) => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) return resolve(null);
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve({ user: userFromSession(session), token: session.getIdToken().getJwtToken() });
    });
  });
}

export function signOut(): void {
  pool.getCurrentUser()?.signOut();
}
